// src/generators/dzial1_liczby_i_dzialania/temat5_teoria_liczb/podtemat2_liczby_pierwsze_types/PrimeFactorizationBasicTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FactorProductInput, { parseFactorProductInput, factorArrayToLatex } from "../../../../components/inputs/FactorProductInput";

// ===== helpers =====
const randInt = (a, b) => a + Math.floor(Math.random() * (b - a + 1));

// uniwersalna faktoryzacja dzieleniem próbnym (nie zależymy od listy primes)
function primeFactors(n) {
  const f = [];
  while (n % 2 === 0) { f.push(2); n /= 2; }
  let p = 3;
  while (p * p <= n) {
    while (n % p === 0) { f.push(p); n /= p; }
    p += 2;
  }
  if (n > 1) f.push(n);
  return f;
}

// wszystkie czynniki < 50 (czyli ≤ 47)
function allFactorsBelow50(factors) {
  return factors.every((p) => p < 50);
}

/**
 * Sprawdza, czy rozkład spełnia nasze „dydaktyczne” zasady:
 * - 2,3,5: krotność ≤ 4
 * - 7,11,13: krotność ≤ 2
 * - 17,19,...,47: krotność ≤ 1
 * - w całym rozkładzie maksymalnie jedna liczba pierwsza > 5
 * - wszystkie czynniki < 50
 */
function factorsProfileOk(factors) {
  if (!allFactorsBelow50(factors)) return false;

  const counts = new Map();
  for (const p of factors) {
    counts.set(p, (counts.get(p) || 0) + 1);
  }

  const hardPrimes = []; // zebrane wszystkie „trudniejsze” > 5

  for (const [p, c] of counts.entries()) {
    if (p === 2 || p === 3 || p === 5) {
      // łatwe: mogą wystąpić do 4 razy
      if (c > 4) return false;
    } else if (p === 7 || p === 11 || p === 13) {
      // średnie: maksymalnie kwadrat (2 razy)
      if (c > 2) return false;
      hardPrimes.push(p);
    } else {
      // większe: 17,19,...,47 – tylko pojedynczo
      if (c > 1) return false;
      hardPrimes.push(p);
    }
  }

  // nie chcemy kombinacji kilku trudnych liczb pierwszych typu 7·11, 11·47 itd.
  if (hardPrimes.length > 1) return false;

  return true;
}

// losowanie liczby:
// - zakres podany z zewnątrz (2-,3-,4-cyfrowe)
// - 3–8 czynników,
// - profil czynników zgodny z factorsProfileOk
function generateNumber(min, max) {
  let n, factors, tries = 0;
  do {
    n = randInt(min, max);
    factors = primeFactors(n);
    tries++;
  } while (
    (
      factors.length < 3 ||
      factors.length > 8 ||
      !factorsProfileOk(factors)
    ) && tries < 500
  );
  return { n, factors };
}

// ===== generators =====
export function generateTask() {
  return generateTaskWith({ variant: "2digit" });
}

export function generateTaskWith({ variant }) {
  let range;
  switch (variant) {
    case "3digit": range = [100, 999]; break;
    case "4digit": range = [1000, 9999]; break;
    default:       range = [10, 99];
  }
  const { n, factors } = generateNumber(range[0], range[1]);
  return {
    type: "PrimeFactorizationBasicTask",
    data: { number: n, factors, variant }
  };
}

// ===== render task =====
export function renderTask({ data }) {
  return (
    <div style={{ marginBottom: 16 }}>
      Rozłóż liczbę <InlineMath math={String(data.number)} /> na czynniki pierwsze.
      <br />
      <span className="text-sm text-neutral-500">
        Zapisz jako iloczyn liczb pierwszych, np. <InlineMath math={'2\\cdot2\\cdot3\\cdot5'} />.
      </span>
    </div>
  );
}

// ===== input =====
export function renderInput({ data }, userAnswer, onChange, disabled) {
  const val = (userAnswer && typeof userAnswer === "object") ? userAnswer : { raw: "", parsed: undefined };
  const set = (v) => onChange?.(v);
  return (
    <FactorProductInput
      value={val}
      onChange={set}
      disabled={disabled}
      placeholder="np. 2·2·3·5"
      ariaLabel="iloczyn czynników pierwszych"
      showPreview
    />
  );
}

// ===== check =====
export function checkAnswer({ data }, userAnswer) {
  const parsed = (userAnswer && typeof userAnswer === "object")
    ? (userAnswer.parsed ?? parseFactorProductInput(userAnswer.raw))
    : parseFactorProductInput(userAnswer);

  if (parsed === undefined || parsed === null) return false;

  const correct = [...data.factors].sort((a, b) => a - b);
  if (parsed.length !== correct.length) return false;
  for (let i = 0; i < parsed.length; i++) {
    if (parsed[i] !== correct[i]) return false;
  }
  return true;
}

// ===== answers =====
export function renderAnswer({ data }) {
  return <InlineMath math={data.factors.join("\\cdot")} />;
}

export function renderUserAnswer(userAnswer) {
  const parsed = (userAnswer && typeof userAnswer === "object")
    ? (userAnswer.parsed ?? parseFactorProductInput(userAnswer.raw))
    : parseFactorProductInput(userAnswer);

  if (parsed === undefined) return <i>(brak odpowiedzi)</i>;
  if (parsed === null)      return <i>(niepoprawny zapis)</i>;
  return <InlineMath math={factorArrayToLatex(parsed)} />;
}

// ===== explanation =====
// „Słupkowy” zapis: dwie kolumny – po lewej kolejne wyniki, po prawej dzielnik (liczba pierwsza).
export function renderExplanation({ data }) {
  const { number, factors } = data;

  // zbuduj wiersze array: "n & p \\" ... aż do 1
  let n = number;
  const rows = factors.map((p) => {
    const row = `${n} & ${p} \\\\`;
    n = n / p;
    return row;
  });
  // ostatni wiersz z 1 i pustą drugą kolumną
  rows.push(`1 & \\\\`);

  const ladderLatex = `\\begin{array}{r|l}
${rows.join("\n")}
\\end{array}`;

  const productLatex = `${number} = ${factors.join("\\cdot")}`;

  return (
    <div style={{ display: "grid", rowGap: 10 }}>
      <p><b>Krok 1:</b> Dziel kolejno przez najmniejsze dzielniki pierwsze (zapis „słupkowy”).</p>
      <BlockMath math={ladderLatex} />
      <p><b>Krok 2:</b> Odczytaj iloczyn czynników pierwszych:</p>
      <InlineMath math={productLatex} />
    </div>
  );
}
